import { FC, useMemo } from 'react';
import { Link as RouterLink } from 'react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ApprovalCard from 'src/components/SupportCase/ApprovalCard';
import { SentAttachmentChips } from 'src/components/SupportCase/AttachmentChips';
import CitationList from 'src/components/Support/CitationList';
import { JIRA_STATE_META } from 'src/components/SupportCase/stateMeta';
import { fDateTime } from 'src/utils/formatTime';
import type {
  ApprovalRequest,
  CaseEvent as CaseEventType,
  CitedArticle,
  JiraState,
  SupportAttachmentRef,
} from 'src/components/SupportCase/types';

interface CaseEventProps {
  event: CaseEventType;
  /** True when a later approval decision exists for this request. */
  decided?: boolean;
  busy?: boolean;
  onDecide: (approvalId: string, decision: 'confirm' | 'decline') => void;
  /** Files still in the case, so a removed one renders as "removed". */
  availableDocumentIds?: Set<number>;
}

const USER_EVENTS = new Set(['user_message', 'reopened']);
const MUTED_EVENTS = new Set([
  'approval_granted',
  'approval_declined',
  'approval_expired',
  'agent_action',
  'jira_comment',
  'jira_status',
  'closed',
]);
/**
 * Muted events are a quiet one-liner, and `closed` is the reason that rule exists:
 * the agent already said all of this in the answer directly above, so printing the
 * resolve summary in full showed the same paragraph twice. The whole text stays in
 * `title`, so nothing is lost — it is just not read out twice.
 */
const ONE_LINE_CHARS = 160;

/**
 * The three words, and only those (ENG-4185).
 *
 * A chip rather than the event's message, because the message is fixed copy and the
 * state is the fact. Colours come from the status registry's semantics — pending is
 * neutral, in progress is informational, done is success — and never from a raw Jira
 * status, which Thor no longer sends.
 */

const oneLine = (text: string): string => {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > ONE_LINE_CHARS
    ? `${flat.slice(0, ONE_LINE_CHARS).trimEnd()}…`
    : flat;
};

const PROBLEM_EVENTS = new Set([
  'agent_error',
  'tool_failed',
  'tool_refused',
  'jira_pending',
  'guardrail_blocked',
]);

/**
 * One row in the case timeline (ENG-4137).
 *
 * The shape follows what the row *means*, not which table it came from:
 *
 * * the user's own words read as a message (right-aligned, no chrome);
 * * the agent's answer reads as prose;
 * * an approval request becomes the card that can authorise it;
 * * everything else — actions, Jira transitions, errors — is a quiet one-liner,
 *   because a case is mostly a record of small facts.
 *
 * `agent_error` and `jira_pending` are deliberately visible rather than swallowed:
 * a case that quietly failed is worse than one that says it failed.
 */
const CaseEvent: FC<CaseEventProps> = ({
  event,
  decided = false,
  busy = false,
  onDecide,
  availableDocumentIds,
}) => {
  /**
   * The articles this answer was built from (ENG-4211).
   *
   * Thor folds them from the tool calls the turn actually made, so this is a record of
   * what was read, not of what the model said it read. A malformed payload renders no
   * strip rather than throwing: the answer above it is the thing the user is waiting
   * for, and `CitationCard` re-checks the audience server-side when it fetches.
   */
  const cited = useMemo<CitedArticle[]>(() => {
    const rows = event.payload?.cited_articles;
    if (!Array.isArray(rows)) {
      return [];
    }
    return rows.filter(
      (row): row is CitedArticle =>
        typeof (row as CitedArticle)?.id === 'number' &&
        typeof (row as CitedArticle)?.title === 'string'
    );
  }, [event]);

  const isUser = USER_EVENTS.has(event.event_type);
  const isMuted = MUTED_EVENTS.has(event.event_type);
  const isProblem = PROBLEM_EVENTS.has(event.event_type);

  const approval = useMemo<ApprovalRequest | null>(() => {
    if (event.event_type !== 'approval_requested') {
      return null;
    }
    const payload = event.payload || {};
    const approvalId = payload.approval_id;
    if (typeof approvalId !== 'string') {
      return null;
    }
    return {
      approvalId,
      tool: String(payload.tool || ''),
      preview: String(
        payload.preview || event.message || 'Confirm this change'
      ),
      rationale: String(payload.rationale || ''),
      expiresInSeconds: Number(payload.expires_in_seconds || 600),
      eventId: event.id,
    };
  }, [event]);

  if (approval) {
    return (
      <Box>
        <ApprovalCard
          approval={approval}
          decided={decided}
          busy={busy}
          onDecide={onDecide}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5 }}
        >
          {fDateTime(event.created_at)}
        </Typography>
      </Box>
    );
  }

  if (event.event_type === 'attachment') {
    const rows = Array.isArray(event.payload?.attachments)
      ? (event.payload.attachments as SupportAttachmentRef[])
      : [];
    if (rows.length === 0) {
      return null;
    }
    const fromAgent =
      event.payload?.origin === 'agent' ||
      rows.some((row) => row.origin === 'agent');
    return (
      <Stack spacing={0.5} data-testid={`case-event-attachment-${event.id}`}>
        <Typography variant="caption" color="text.secondary">
          {fromAgent ? 'Pronto Assistant added' : 'You attached'}{' '}
          {rows.length === 1 ? 'a document' : `${rows.length} documents`} ·{' '}
          {fDateTime(event.created_at)}
        </Typography>
        <SentAttachmentChips
          attachments={rows}
          availableIds={availableDocumentIds}
        />
      </Stack>
    );
  }

  if (event.event_type === 'jira_status') {
    const state = String(event.payload?.to ?? '') as JiraState;
    const meta = JIRA_STATE_META[state as Exclude<JiraState, ''>];
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        data-testid={`case-event-jira-status-${event.id}`}
      >
        <Chip
          size="small"
          variant="outlined"
          color={meta?.color ?? 'default'}
          label={meta?.label ?? 'Update from Pronto Housing Support'}
        />
        <Typography variant="caption" color="text.secondary">
          {fDateTime(event.created_at)}
        </Typography>
      </Stack>
    );
  }

  if (event.event_type === 'jira_filed') {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {event.message || 'Sent to Pronto Housing Support'}
          {event.author ? ` · ${event.author}` : ''} ·{' '}
          {fDateTime(event.created_at)}
        </Typography>
      </Stack>
    );
  }

  if (isUser) {
    return (
      <Stack alignItems="flex-end" spacing={0.5}>
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            maxWidth: '85%',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {event.message}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          You · {fDateTime(event.created_at)}
        </Typography>
      </Stack>
    );
  }

  if (isMuted) {
    const text = event.message || event.event_type;
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <InfoOutlinedIcon fontSize="inherit" color="disabled" />
        <Typography
          variant="caption"
          color="text.secondary"
          title={text}
          sx={{ display: 'block' }}
        >
          {oneLine(text)}
          {event.author ? ` · ${event.author}` : ''} ·{' '}
          {fDateTime(event.created_at)}
        </Typography>
      </Stack>
    );
  }

  if (isProblem) {
    // ENG-4251 (PA-07) — a failed *job* is not the turn's outcome. The turn can have
    // answered and still failed to produce a document; rendering that as a terminal
    // red block is what made one turn look failed, busy and succeeded at once.
    const isJobFailure = event.payload?.origin === 'job';
    return (
      <Box
        sx={{
          borderLeft: 3,
          borderColor: isJobFailure ? 'warning.main' : 'error.main',
          pl: 1.5,
          py: 0.5,
        }}
        data-testid={isJobFailure ? 'case-event-job-note' : 'case-event-error'}
      >
        <Typography
          variant="body2"
          color={
            event.payload?.origin === 'job' ? 'text.secondary' : 'error.dark'
          }
          sx={{ whiteSpace: 'pre-wrap' }}
        >
          {event.payload?.origin === 'job'
            ? `The report did not finish. Ask again and it will retry. ${event.message}`.trim()
            : event.message}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {(event.payload?.error_code as string) || event.event_type} ·{' '}
          {fDateTime(event.created_at)}
        </Typography>
      </Box>
    );
  }

  // The agent's answer, and anything else that is prose.
  //
  // ENG-4211 — where the answer's links go: inside the product an href is a route (the
  // article opens without a page load and Back works), anything else opens in a new tab
  // with noopener. `react-markdown` sanitises `javascript:`/`data:` itself and no
  // `rehype-raw` is mounted, so this override only decides how a safe href is followed.
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip label="Pronto Assistant" size="small" variant="outlined" />
        <Typography variant="caption" color="text.secondary">
          {fDateTime(event.created_at)}
        </Typography>
      </Stack>
      <Box
        sx={{
          typography: 'body2',
          '& p': { m: 0, mb: 1 },
          '& p:last-of-type': { mb: 0 },
          '& ul, & ol': { mt: 0.5, mb: 1, pl: 2.5 },
          '& li': { mb: 0.25 },
          '& li > p': { mb: 0 },
          '& strong': { fontWeight: 700 },
          '& code': {
            px: 0.5,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
            fontFamily: 'monospace',
            fontSize: '0.9em',
          },
          '& a': { color: 'primary.main' },
          '& h1, & h2, & h3, & h4': {
            typography: 'body2',
            fontWeight: 700,
            m: 0,
            mt: 1,
            mb: 0.5,
          },
          '& > *:first-of-type': { mt: 0 },
          '& table': { borderCollapse: 'collapse', mb: 1 },
          '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5 },
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children }) =>
              (href ?? '').startsWith('/workbench/') ? (
                <RouterLink to={href ?? ''}>{children}</RouterLink>
              ) : (
                <Link href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </Link>
              ),
          }}
        >
          {event.message}
        </ReactMarkdown>
      </Box>
      {cited.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 0.5 }} data-testid="case-event-sources">
          <Typography variant="caption" color="text.secondary">
            Sources
          </Typography>
          {/* PA-05 — three rows, then "+N more"; never a stack that buries the answer. */}
          <CitationList articles={cited} testId="case-event-citations" />
        </Stack>
      )}
    </Stack>
  );
};

export default CaseEvent;
